import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BaseCardComponent } from '../../atoms/cards/base-card/base-card.component';
import { ActionCardIconComponent } from '../../atoms/cards/action-card/action-card-icon.component';
import { LinkComponent } from '../../atoms/link/link.component';

@Component({
  selector: 'ui-alarms-priority-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    BaseCardComponent,
    ActionCardIconComponent,
    LinkComponent,
  ],
  template: `
    <ng-container *transloco="let t">
      <ui-base-card [class]="'justify-between !items-start'" [reverse]="true">
        <ui-action-card-icon icon class="{{ iconClass() }}" [icon]="icon()" />
        <ng-container content>
          <div class="flex flex-col gap-y-3">
            <div class="flex gap-x-11">
              @for (item of alarmsItems(); track $index) {
                <div [class]="'flex flex-col ' + item?.class">
                  <span class="text-xs text-gray-400 font-medium">
                    {{ item.label }}
                  </span>
                  <span class="text-2xl">
                    {{ item.count ?? '--' }}
                  </span>
                </div>
              }
            </div>
            <h1 class="text-md">
              {{ t('ALARMS.LABEL') }}
            </h1>
            <ui-link [icon]="'export'" (linkClick)="handleAlarmsLinkClick()">
              <span class="text-xxs text-neutral-8 underline">
                {{ t('ALARMS.VIEW_ALARMS') }}
              </span>
            </ui-link>
          </div>
        </ng-container>
      </ui-base-card>
    </ng-container>
  `,
})
export class AlarmsPriorityCardComponent {
  private translocoService = inject(TranslocoService);

  alarmsLowPriorityCount = input<number | null>(null);
  alarmsMediumPriorityCount = input<number | null>(null);
  alarmsHighPriorityCount = input<number | null>(null);

  alarmsLinkClick = output<void>();

  lowPriorityLabel = toSignal(
    this.translocoService.selectTranslate('ALARMS.LOW'),
  );
  mediumPriorityLabel = toSignal(
    this.translocoService.selectTranslate('ALARMS.MEDIUM'),
  );
  highPriorityLabel = toSignal(
    this.translocoService.selectTranslate('ALARMS.HIGH'),
  );

  iconClass = computed(() => {
    const low = this.alarmsLowPriorityCount();
    const medium = this.alarmsMediumPriorityCount();
    const high = this.alarmsHighPriorityCount();

    if (low === null && medium === null && high === null) {
      return 'text-neutral-7 bg-neutral-3 shadow-custom-neutral-6';
    }

    return (low ?? 0) > 0 || (medium ?? 0) > 0 || (high ?? 0) > 0
      ? 'text-error bg-error-light shadow-custom-error'
      : 'text-green bg-green-light shadow-custom-green';
  });

  icon = signal<string>('icons:warning');

  alarmsItems = computed(() => [
    {
      label: this.highPriorityLabel(),
      count: this.alarmsHighPriorityCount(),
      class: this.getItemClasses(this.alarmsHighPriorityCount()),
    },
    {
      label: this.mediumPriorityLabel(),
      count: this.alarmsMediumPriorityCount(),
      class: this.getItemClasses(this.alarmsMediumPriorityCount()),
    },
    {
      label: this.lowPriorityLabel(),
      count: this.alarmsLowPriorityCount(),
      class: this.getItemClasses(this.alarmsLowPriorityCount()),
    },
  ]);

  handleAlarmsLinkClick() {
    this.alarmsLinkClick.emit();
  }

  getItemClasses(count: number | null) {
    if (count === null) return 'text-neutral-6';
    return count <= 0 ? 'text-green' : 'text-error';
  }
}
